/**
 * V75 — SEO Audit Dashboard (Phase 5: Continuous SEO Framework).
 *
 * Admin-only page at /admin/seo-audit (gated by ProtectedAdminRoute +
 * super-mode via the backend /api/admin/seo/* endpoints).
 *
 * Two tabs:
 *   • Link Audit   — HTTP status probe of backend endpoints + frontend spot-check.
 *   • Content Gap  — duplicate URLs / bucket misclassification / coverage gaps
 *                    detected from the sitemap buckets.
 *
 * Fully additive — reads existing api client (which already carries the JWT
 * and X-Super-Mode-Token headers). No new backend routes beyond what
 * server.py already exposes: /api/admin/seo/audit/{latest,history,run}.
 */

import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, RefreshCw, PlayCircle, Loader2, CheckCircle2, AlertTriangle,
    XCircle, Clock, Layers, Bug, FileWarning, Link2, Activity, ExternalLink,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { SuperModeProvider, useSuperMode } from "../../context/SuperModeContext";

function StatCard({ icon: Icon, label, value, tone = "slate", subtitle, testid }) {
    const tones = {
        slate: "bg-slate-50 border-slate-200 text-slate-900",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
        rose: "bg-rose-50 border-rose-200 text-rose-900",
        blue: "bg-blue-50 border-blue-200 text-blue-900",
    };
    return (
        <div data-testid={testid} className={`rounded-xl border p-4 ${tones[tone]}`}>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold opacity-80">
                <Icon className="w-3.5 h-3.5" /> {label}
            </div>
            <div className="mt-1 font-heading text-2xl font-bold">{value}</div>
            {subtitle && <div className="text-[11px] mt-1 opacity-70">{subtitle}</div>}
        </div>
    );
}

function Section({ title, subtitle, right, children }) {
    return (
        <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">
            <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
                <div>
                    <h3 className="font-heading font-bold text-lg text-slate-900">{title}</h3>
                    {subtitle && <p className="text-slate-500 text-xs mt-0.5">{subtitle}</p>}
                </div>
                {right}
            </div>
            {children}
        </section>
    );
}

function EmptyState({ text }) {
    return (
        <div className="py-6 text-center text-sm text-slate-500 flex items-center gap-2 justify-center">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" /> {text}
        </div>
    );
}

export default function SeoAuditDashboardWrapper() {
    return (
        <SuperModeProvider>
            <SeoAuditDashboard />
        </SuperModeProvider>
    );
}

function SeoAuditDashboard() {
    const navigate = useNavigate();
    const superMode = useSuperMode();

    const [tab, setTab] = useState("link");
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [run, setRun] = useState(null);
    const [history, setHistory] = useState([]);

    const fetchLatest = useCallback(async () => {
        setLoading(true);
        try {
            const [latest, hist] = await Promise.all([
                api.get("/admin/seo/audit/latest"),
                api.get("/admin/seo/audit/history?limit=10"),
            ]);
            setRun(latest.data?.run || null);
            setHistory(hist.data?.runs || []);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail === "super_mode_required") {
                toast.error("Super Mode is required to view SEO Audit. Unlock it from the admin dashboard.");
            } else {
                toast.error(formatApiError(detail) || "Failed to load audit.");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLatest();
    }, [fetchLatest]);

    async function runAudit() {
        if (running) return;
        setRunning(true);
        try {
            const res = await api.post("/admin/seo/audit/run");
            setRun(res.data?.run || null);
            toast.success("Audit complete.");
            // Refresh history in the background
            api.get("/admin/seo/audit/history?limit=10")
                .then((h) => setHistory(h.data?.runs || []))
                .catch(() => { /* ignore */ });
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail === "super_mode_required") {
                toast.error("Super Mode is required. Unlock it from the admin dashboard.");
            } else {
                toast.error(formatApiError(detail) || "Audit failed.");
            }
        } finally {
            setRunning(false);
        }
    }

    const la = run?.link_audit || {};
    const cg = run?.content_gap || {};
    const statusCounts = la.status_counts || { "2xx": 0, "3xx": 0, "4xx": 0, "5xx": 0, error: 0 };

    return (
        <div className="min-h-screen bg-slate-50" data-testid="seo-audit-dashboard">
            {/* Top bar */}
            <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
                    <button
                        onClick={() => navigate("/admin/dashboard")}
                        data-testid="seo-audit-back"
                        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <div className="h-4 w-px bg-slate-200" />
                    <h1 className="font-heading font-bold text-lg text-slate-900">SEO Audit</h1>
                    <span className="text-[11px] uppercase tracking-wider font-bold text-slate-400">
                        Phase 5 · Continuous SEO
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            onClick={fetchLatest}
                            disabled={loading || running}
                            data-testid="seo-audit-refresh"
                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                        >
                            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            Refresh
                        </button>
                        <button
                            onClick={runAudit}
                            disabled={running || !superMode?.active}
                            data-testid="seo-audit-run"
                            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50"
                            title={!superMode?.active ? "Super Mode required to run an audit" : "Run audit now"}
                        >
                            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                            Run Now
                        </button>
                    </div>
                </div>
                {/* Tabs */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1">
                    {[
                        { id: "link", label: "Link Audit", icon: Link2 },
                        { id: "content", label: "Content Gap", icon: FileWarning },
                        { id: "history", label: "History", icon: Clock },
                    ].map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            data-testid={`seo-audit-tab-${id}`}
                            onClick={() => setTab(id)}
                            className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px inline-flex items-center gap-1.5 ${
                                tab === id
                                    ? "border-slate-900 text-slate-900"
                                    : "border-transparent text-slate-500 hover:text-slate-900"
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5" /> {label}
                        </button>
                    ))}
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
                {!run && !loading && (
                    <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center bg-white">
                        <Activity className="w-6 h-6 text-slate-400 mx-auto mb-3" />
                        <h3 className="font-heading font-bold text-lg mb-1">No audit runs yet</h3>
                        <p className="text-sm text-slate-500 mb-4">Click <strong>Run Now</strong> to trigger the first SEO audit.</p>
                    </div>
                )}

                {run && (
                    <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-2">Latest audit</div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <StatCard
                                testid="stat-total-urls"
                                icon={Layers} label="URLs in sitemap" value={run.total_urls || 0}
                                subtitle={`${Object.keys(cg.per_bucket_counts || {}).length} buckets`}
                            />
                            <StatCard
                                testid="stat-broken"
                                icon={XCircle} label="Broken links"
                                value={la.broken_count || 0}
                                tone={la.broken_count > 0 ? "rose" : "emerald"}
                                subtitle={`${la.backend_probed || 0} backend + ${la.frontend_spot_checked || 0} spot-check`}
                            />
                            <StatCard
                                testid="stat-issues"
                                icon={Bug} label="Content issues"
                                value={cg.issue_count || 0}
                                tone={cg.issue_count > 0 ? "amber" : "emerald"}
                                subtitle={`${cg.duplicates_count || 0} dupes · ${cg.misclassified_count || 0} misclassified`}
                            />
                            <StatCard
                                testid="stat-duration"
                                icon={Clock} label="Duration"
                                value={`${((run.duration_ms || 0) / 1000).toFixed(1)}s`}
                                subtitle={run.triggered_by === "admin" ? "Manual run" : "Scheduled"}
                            />
                        </div>
                        <div className="mt-3 text-[11px] text-slate-500">
                            Started {new Date(run.started_at).toLocaleString()} · run id {run.id?.slice(0, 8)}
                        </div>
                    </div>
                )}

                {tab === "link" && run && (
                    <div className="space-y-5">
                        <Section
                            title="HTTP status distribution"
                            subtitle="Backend-served endpoints (sitemaps, robots, llms, /api) + a 15-URL frontend spot-check."
                        >
                            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                                <StatCard testid="link-2xx" icon={CheckCircle2} label="2xx" value={statusCounts["2xx"] || 0} tone="emerald" />
                                <StatCard testid="link-3xx" icon={ExternalLink} label="3xx" value={statusCounts["3xx"] || 0} tone="blue" />
                                <StatCard testid="link-4xx" icon={AlertTriangle} label="4xx" value={statusCounts["4xx"] || 0} tone={statusCounts["4xx"] > 0 ? "amber" : "slate"} />
                                <StatCard testid="link-5xx" icon={XCircle} label="5xx" value={statusCounts["5xx"] || 0} tone={statusCounts["5xx"] > 0 ? "rose" : "slate"} />
                                <StatCard testid="link-error" icon={Bug} label="Error" value={statusCounts.error || 0} tone={statusCounts.error > 0 ? "rose" : "slate"} />
                            </div>
                        </Section>

                        <Section title={`Broken URLs (${la.broken_count || 0})`} subtitle="4xx / 5xx / network errors">
                            {(la.broken_urls || []).length === 0 ? (
                                <EmptyState text="All probed endpoints returned 2xx." />
                            ) : (
                                <div className="overflow-x-auto -mx-2">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                                                <th className="py-2 px-2">Status</th>
                                                <th className="py-2 px-2">URL</th>
                                                <th className="py-2 px-2">Response ms</th>
                                                <th className="py-2 px-2">Error</th>
                                            </tr>
                                        </thead>
                                        <tbody data-testid="broken-urls-tbody">
                                            {la.broken_urls.slice(0, 100).map((b, i) => (
                                                <tr key={i} className="border-b border-slate-100">
                                                    <td className="py-1.5 px-2 font-mono font-bold text-rose-700">{b.status_code || "ERR"}</td>
                                                    <td className="py-1.5 px-2 text-slate-700 break-all">{b.url}</td>
                                                    <td className="py-1.5 px-2 text-slate-500">{b.response_ms ?? "—"}</td>
                                                    <td className="py-1.5 px-2 text-slate-500">{b.error || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </Section>

                        <Section title={`Slow URLs (${la.slow_count || 0})`} subtitle="Response > 1500ms">
                            {(la.slow_urls || []).length === 0 ? (
                                <EmptyState text="No slow responses detected." />
                            ) : (
                                <ul className="text-xs text-slate-700 space-y-1">
                                    {la.slow_urls.slice(0, 20).map((s, i) => (
                                        <li key={i} className="font-mono">
                                            <span className="text-amber-700 font-bold mr-2">{s.response_ms}ms</span>
                                            {s.url}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Section>
                    </div>
                )}

                {tab === "content" && run && (
                    <div className="space-y-5">
                        <Section title="Per-bucket URL counts" subtitle="Sitemap URL distribution across the 7 category buckets.">
                            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                                {Object.entries(cg.per_bucket_counts || {}).map(([k, v]) => (
                                    <div key={k} data-testid={`bucket-${k}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{k}</div>
                                        <div className="font-heading text-lg font-bold text-slate-900">{v}</div>
                                    </div>
                                ))}
                            </div>
                        </Section>

                        <Section
                            title={`Duplicate URLs across buckets (${cg.duplicates_count || 0})`}
                            subtitle="A URL should appear in exactly one bucket. Duplicates dilute crawl budget signal."
                        >
                            {(cg.duplicates || []).length === 0 ? (
                                <EmptyState text="No duplicate URLs found." />
                            ) : (
                                <ul className="text-xs text-slate-700 space-y-1 max-h-96 overflow-y-auto" data-testid="duplicates-list">
                                    {cg.duplicates.map((d, i) => (
                                        <li key={i} className="flex items-center gap-2 font-mono">
                                            <span className="text-slate-900">{d.path}</span>
                                            <span className="text-slate-400">→</span>
                                            {d.buckets.map((b) => (
                                                <span key={b} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{b}</span>
                                            ))}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Section>

                        <Section
                            title={`Misclassified URLs (${cg.misclassified_count || 0})`}
                            subtitle="URLs whose slug prefix suggests they belong in a different bucket."
                        >
                            {(cg.misclassified || []).length === 0 ? (
                                <EmptyState text="All URLs are in their expected bucket." />
                            ) : (
                                <ul className="text-xs text-slate-700 space-y-1" data-testid="misclassified-list">
                                    {cg.misclassified.map((m, i) => (
                                        <li key={i} className="font-mono flex items-center gap-2 flex-wrap">
                                            <span className="text-slate-900">{m.path}</span>
                                            <span className="text-slate-400">is in</span>
                                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">{m.actual_bucket}</span>
                                            <span className="text-slate-400">expected</span>
                                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">{m.expected_bucket}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Section>

                        <Section
                            title={`Tutor-job coverage gaps (${cg.tutor_coverage_gaps?.length || 0})`}
                            subtitle="Missing tutor-job variants for Jabalpur / Bhopal / Nagpur (33 variants each expected)."
                        >
                            {(cg.tutor_coverage_gaps || []).length === 0 ? (
                                <EmptyState text="All 99 required tutor-job URLs are present." />
                            ) : (
                                <div data-testid="coverage-gaps" className="space-y-3">
                                    {cg.tutor_coverage_gaps.map((g) => (
                                        <div key={g.city} className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                            <div className="font-heading font-bold text-sm text-amber-900 capitalize mb-1">
                                                {g.city} — {g.missing_paths.length} missing
                                            </div>
                                            <ul className="text-[11px] text-amber-800 font-mono space-y-0.5">
                                                {g.missing_paths.slice(0, 20).map((p) => (
                                                    <li key={p}>{p}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Section>
                    </div>
                )}

                {tab === "history" && (
                    <Section title="Recent audit runs" subtitle="Last 10 audit runs from the scheduler and manual triggers.">
                        {history.length === 0 ? (
                            <EmptyState text="No historical runs yet." />
                        ) : (
                            <div className="overflow-x-auto -mx-2">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                                            <th className="py-2 px-2">Started</th>
                                            <th className="py-2 px-2">Trigger</th>
                                            <th className="py-2 px-2">URLs</th>
                                            <th className="py-2 px-2">Broken</th>
                                            <th className="py-2 px-2">Issues</th>
                                            <th className="py-2 px-2">Duration</th>
                                        </tr>
                                    </thead>
                                    <tbody data-testid="history-tbody">
                                        {history.map((h) => (
                                            <tr key={h.id} className="border-b border-slate-100">
                                                <td className="py-1.5 px-2 text-slate-700">{new Date(h.started_at).toLocaleString()}</td>
                                                <td className="py-1.5 px-2">
                                                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${h.triggered_by === "admin" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700"}`}>{h.triggered_by}</span>
                                                </td>
                                                <td className="py-1.5 px-2 font-mono text-slate-700">{h.total_urls}</td>
                                                <td className={`py-1.5 px-2 font-mono ${h.link_audit?.broken_count > 0 ? "text-rose-700 font-bold" : "text-emerald-700"}`}>{h.link_audit?.broken_count ?? "—"}</td>
                                                <td className={`py-1.5 px-2 font-mono ${h.content_gap?.issue_count > 0 ? "text-amber-700 font-bold" : "text-emerald-700"}`}>{h.content_gap?.issue_count ?? "—"}</td>
                                                <td className="py-1.5 px-2 text-slate-500">{((h.duration_ms || 0) / 1000).toFixed(1)}s</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Section>
                )}

                <div className="text-center text-[11px] text-slate-400 pt-4">
                    <Link to="/admin/dashboard" className="hover:text-slate-600">← Back to Admin Dashboard</Link>
                </div>
            </main>
        </div>
    );
}
